import React, { useState, useEffect } from "react";

import { connect } from "react-redux";
import { useIntl } from "react-intl";

import Card from "../../UI/Card/Card";
import Button from "../../UI/Button/Button";
import ErrorPanel, { ErrorSize } from "../../UI/ErrorPanel/ErrorPanel";

import { faEdit } from "@fortawesome/free-solid-svg-icons";

import * as InvoicesActions from "../../../middleware/Store/Actions/InvoicesActions";
import countries from "../../../middleware/Constants/CountriesList";
import usStates from "../../../middleware/Constants/USStatesList";
import canStates from "../../../middleware/Constants/CANStatesList";

import * as Utils from "../../Utils/BillingUtils";
import LogService from "../../../middleware/CommonSdk/LogService";

const LOG_TAG = "INVOICES_ADDRESS";

const InvoicesAddress = (props) => {

    const [progress, setProgress] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [account, setAccount] = useState(null);
    const [taxInfo, setTaxInfo] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        LogService.debug(LOG_TAG, "useEffect account");
        if (props.account?.data?.billToContact) {
            setAccount(props.account.data.billToContact);
            LogService.info(LOG_TAG, "useEffect account country", props.account?.data?.billToContact?.country)
        }

        if (props.account?.data?.taxInfo)
            setTaxInfo(props.account.data.taxInfo);
    }, [props.account]);

    const handleChangeAddress = (e) => {
        const { name, value } = e.target;
        setAccount(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleChangeVAT = (e) => {
        const value = e.target.value;
        setTaxInfo({ VATId: value });//remove rest of content, to clear checkVat as modified
    }

    const handleSubmitAddress = (e) => {
        LogService.info(LOG_TAG, "handleSubmitAddress");

        setSubmitted(true);
        setProgress(true);
        let data = {};
        data.billToContact = account;
        if (taxInfo?.VATId)
            data.taxInfo = { VATId: taxInfo.VATId };

        props.updateInvoiceInformation(props.user?.id, data)
            .then(() => {
                LogService.trace(LOG_TAG, "handleSubmitAddress success");
                setProgress(false);
                setErrorMessage("");
            })
            .catch(errorPayload => {
                LogService.error(LOG_TAG, "handleSubmitAddress error");
                setProgress(false);
                setErrorMessage(errorPayload.errorMessage);
                if (errorPayload.errorCode === 400306) //vat not valid
                    setTaxInfo(prevState => ({
                        ...prevState,
                        checkVat: { status: "invalid" }
                    }));
            });
    }

    LogService.info(LOG_TAG, "Enter");

    const intl = useIntl();
    const title = intl.messages.invoicesInfo;
    const subtitle = intl.messages.invoicesInfoMessage;
    const companyName = props.account?.data?.basicInfo?.name || "";

    const countriesOptions = countries.map(country => {
        return (
            <option value={country["alpha-3"]} key={country["alpha-3"]}>
                {Utils.capitalizeFirstLetter(country.name)}
            </option>
        );
    });

    let usStatesOptions = usStates.map(state => {
        return (
            <option value={state.abbreviation} key={state.abbreviation}>
                {Utils.capitalizeFirstLetter(state.name)}
            </option>
        );
    });

    let canStatesOptions = canStates.map(state => {
        return (
            <option value={state.abbreviation} key={state.abbreviation}>
                {Utils.capitalizeFirstLetter(state.name)}
            </option>
        );
    });

    let vatStatusMessage = Utils.getVatStatusMessage(taxInfo, intl);
    let country = Utils.getCountryName(account?.country);
    let state = Utils.getStateName(account?.state);
    let isVATDisplay = Utils.isVATDisplay(account?.country);

    let isValidable = Utils.isInvoiceAccountFullyFilled({
        data: {
            billToContact: account,
            basicInfo: { name: companyName },
            taxInfo: { VATId: taxInfo?.VATId }
        }
    });

    const isVatError = Utils.isVatError(taxInfo);
    const isVatSuccess = Utils.isVatSuccess(taxInfo);

    let zoneFooter = <div className="form-row align-items-center">
        <div className="col-md-5">
            <Button
                label={intl.messages.update}
                icon={faEdit}
                spinner={progress}
                disabled={!isValidable || progress}
                onClick={handleSubmitAddress}
                className="btn-submit"
            />
        </div>

        {(errorMessage.length > 0) &&
            <div className="col-md-7">
                <ErrorPanel id="server-error" size={ErrorSize.Small}>{errorMessage}</ErrorPanel>
            </div>
        }
    </div>

    return (
        <Card title={title} subtitle={subtitle} action={zoneFooter}>
            <div className="row">
                <div className="col">
                    <div className="form-group">
                        <label htmlFor="inputCompanyName" className="input-label">{intl.messages.company} <small>{intl.messages.invoicesMandatory}</small></label>
                        <input
                            id="inputCompanyName"
                            value={companyName}
                            type="text"
                            placeholder=""
                            className="form-control"
                            autoComplete="off"
                            disabled
                        />
                    </div>

                    {/* VAT */}
                    {isVATDisplay && (
                        <div className="form-row">
                            <div className="col-md-5">
                                <div className="form-group">
                                    <label htmlFor="inputVAT" className="input-label">{intl.messages.invoicesVAT} <small>{intl.messages.invoicesMandatory}</small></label>
                                    <input
                                        id="inputVAT"
                                        name="VATId"
                                        value={taxInfo?.VATId || ""}
                                        type="text"
                                        placeholder=""
                                        className={"form-control " + (isVatSuccess ? "is-valid" : (isVatError ? "is-invalid" : ""))}
                                        autoComplete="off"
                                        onChange={handleChangeVAT}
                                    />
                                    <div id="vatFeedbackMessage" className={isVatError ? "invalid-feedback" : "valid-feedback"}>{vatStatusMessage}</div>
                                    <div id="vatFeedbackError" className="invalid-feedback">{intl.messages.invoicesVATError}</div>
                                </div>
                            </div>
                            <div className="col-md-7">
                                <div className="form-group">
                                    <label></label>
                                    <div className="panel bg-fog">{intl.messages.invoicesVATMessage}</div>
                                </div>
                            </div>
                        </div>)
                    }

                    {/* FirstName, LastName */}
                    <div className="form-row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="inputFirstName" className="input-label">{intl.messages.invoicesFirstName} <small>{intl.messages.invoicesMandatory}</small></label>
                                <input
                                    id="inputFirstName"
                                    name="firstName"
                                    value={account?.firstName || ""}
                                    type="text"
                                    placeholder=""
                                    className={(submitted && !account?.firstName) ? "form-control is-invalid" : "form-control"}
                                    autoComplete="off"
                                    onChange={handleChangeAddress}
                                />
                                <div id="firstNameFeedbackMessage" className="invalid-feedback">{intl.messages.fieldIsRequired}</div>

                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="inputLastName" className="input-label">{intl.messages.invoicesLastName} <small>{intl.messages.invoicesMandatory}</small></label>
                                <input
                                    id="inputLastName"
                                    name="lastName"
                                    value={account?.lastName || ""}
                                    type="text"
                                    placeholder=""
                                    className={(submitted && !account?.lastName) ? "form-control is-invalid" : "form-control"}
                                    autoComplete="off"
                                    onChange={handleChangeAddress}
                                />
                                <div id="lastNameFeedbackMessage" className="invalid-feedback">{intl.messages.fieldIsRequired}</div>
                            </div>
                        </div>
                    </div>

                    {/* Adresse */}
                    <div className="form-group">
                        <label htmlFor="inputAdress1" className="input-label">{intl.messages.invoicesAddress} <small>{intl.messages.invoicesMandatory}</small></label>
                        <input
                            id="inputAdress1"
                            name="address1"
                            value={account?.address1 || ""}
                            type="text"
                            placeholder=""
                            className={(submitted && !account?.address1) ? "form-control is-invalid" : "form-control"}
                            autoComplete="off"
                            onChange={handleChangeAddress}
                        />
                        <input
                            id="inputAdress2"
                            name="address2"
                            value={account?.address2 || ""}
                            type="text"
                            placeholder=""
                            className="form-control mt-2"
                            autoComplete="off"
                            onChange={handleChangeAddress}
                        />
                        <div id="addressFeedbackMessage" className="invalid-feedback">{intl.messages.fieldIsRequired}</div>
                    </div>

                    {/* City, ZipCode */}
                    <div className="form-row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="inputCity" className="input-label">{intl.messages.invoicesCity} <small>{intl.messages.invoicesMandatory}</small></label>
                                <input
                                    id="inputCity"
                                    name="city"
                                    value={account?.city || ""}
                                    type="text"
                                    placeholder=""
                                    className={(submitted && !account?.city) ? "form-control is-invalid" : "form-control"}
                                    autoComplete="off"
                                    onChange={handleChangeAddress}
                                />
                                <div id="cityFeedbackMessage" className="invalid-feedback">{intl.messages.fieldIsRequired}</div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="inputZipcode" className="input-label">{intl.messages.invoicesZipCode} <small>{intl.messages.invoicesMandatory}</small></label>
                                <input
                                    id="inputZipcode"
                                    name="zipCode"
                                    value={account?.zipCode || ""}
                                    type="text"
                                    placeholder=""
                                    className={(submitted && !account?.zipCode) ? "form-control is-invalid" : "form-control"}
                                    autoComplete="off"
                                    onChange={handleChangeAddress}
                                />
                                <div id="zipCodeFeedbackMessage" className="invalid-feedback">{intl.messages.fieldIsRequired}</div>
                            </div>
                        </div>
                    </div>

                    {/* Country, State */}
                    <div className="form-row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="selectCountry" className="input-label">{intl.messages.invoicesCountry}</label>
                                <select
                                    id="selectCountry"
                                    name="country"
                                    className="form-control"
                                    value={country || ""}
                                    onChange={handleChangeAddress}
                                >
                                    {countriesOptions}
                                </select>
                            </div>
                        </div>

                        {Utils.isCanadianOrUsaCountry(country) && (
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label htmlFor="selectState" className="input-label">{intl.messages.invoicesState}</label>
                                    <select
                                        id="selectState"
                                        name="state"
                                        className="form-control"
                                        value={state || ""}
                                        onChange={handleChangeAddress}
                                    >
                                        {Utils.isUsaCountry(country) ? usStatesOptions : canStatesOptions}
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Card>
    )
}

const mapStateToProps = state => {
    return {
        user: state.contacts.user,
        account: state.invoices.account
    };
};

const mapDispatchToProps = dispatch => {
    return {
        updateInvoiceInformation: (userId, data) => dispatch(InvoicesActions.updateInvoiceInformation(userId, data))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(InvoicesAddress)